import React from "react";
import type { ElementType } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { HelmetProvider } from "react-helmet-async";
import "regenerator-runtime/runtime";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-redux-toastr/lib/css/react-redux-toastr.min.css";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import store, { persistor } from "./config/store";
import { AppThemeProvider } from "./helpers/contexts/themeContext";
import MainSEO from "./components/seo/MainSEO";
import ErrorBoundary from "./components/ErrorBoundary";
import { IS_PROD, ENV } from "./config/appConfig";

const MainSEOComponent = MainSEO as ElementType;
const PersistGateComponent = PersistGate as ElementType;
const ProviderComponent = Provider as ElementType;

type WindowWithDataLayer = Window & {
  dataLayer?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

// Initialize GTM
if (ENV.GOOGLE_TAG_MANAGER_ID) {
  (function (
    w: WindowWithDataLayer,
    d: Document,
    s: string,
    l: string,
    i: string
  ) {
    w[l] = w[l] || [];
    (w[l] as Array<Record<string, unknown>>).push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s) as HTMLScriptElement,
      dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode?.insertBefore(j, f);
  })(
    window as unknown as WindowWithDataLayer,
    document,
    "script",
    "dataLayer",
    ENV.GOOGLE_TAG_MANAGER_ID
  );
}

if (IS_PROD) {
  /* eslint-disable no-console */
  // Disable console methods in production except errors
  const noop = () => {};
  console.log = noop;
  console.warn = noop;
  console.info = noop;
  console.debug = noop;
  // Keep console.error for critical issues
}

const root = createRoot(document.getElementById("root")!);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <MainSEOComponent />
      <ProviderComponent store={store}>
        <PersistGateComponent loading={null} persistor={persistor}>
          <ErrorBoundary>
            <AppThemeProvider>
              <App />
            </AppThemeProvider>
          </ErrorBoundary>
        </PersistGateComponent>
      </ProviderComponent>
    </HelmetProvider>
  </React.StrictMode>
);

reportWebVitals();
